import { useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import { persistentAtom } from '@nanostores/persistent';
import useLocation from '@klv/hooks/useLocation';
import getLoggedInStatus from '@klv/context/authentication/utils';
import { getCookieValue, setCookie } from '@klv/shared/utils/cookies';

import type { IAuthenticationStore } from '@klv/context/authentication/types';

const initialState: IAuthenticationStore['authenticationState'] = {
  account: false,
  accountDataLoaded: false,
  landingPageUrl: '',
  loggedIn: false,
  paid: false,
};

const $accountAuthentication = persistentAtom<IAuthenticationStore['authenticationState']>(
  'accountAuthentication',
  initialState,
  {
    encode: JSON.stringify,
    decode: (str: string) => {
      try {
        return JSON.parse(str);
      } catch (e) {
        return initialState;
      }
    },
  });

export const useAuthentication = () => {
  const authenticationState = useStore($accountAuthentication) || initialState;
  
  return {
    authenticationState,
    replace: (value: IAuthenticationStore['authenticationState']) => {
      $accountAuthentication.set(value);
    },
    reset: () => {
      $accountAuthentication.set(initialState);
    },
    setAccount: (value: boolean) => {
      $accountAuthentication.set({ ...authenticationState, account: value });
    },
    setAccountDataLoaded: (value: boolean) => {
      $accountAuthentication.set({ ...authenticationState, accountDataLoaded: value });
    },
    setLoggedIn: (value: boolean) => {
      $accountAuthentication.set({ ...authenticationState, loggedIn: value });
    },
    setPaid: (value: boolean) => {
      $accountAuthentication.set({ ...authenticationState, paid: value });
    },
    setLandingPageUrl: (value: string) => {
      $accountAuthentication.set({ ...authenticationState, landingPageUrl: value });
    },
  };
};

const ONE_YEAR = 31536000;

export const useInitializeLoggedInUser = () => {
  const { authenticationState, replace } = useAuthentication();
  const location = useLocation();
  const [isInitialized, setInitialized] = useState(false);

  useEffect(() => {
    getLoggedInStatus()
      .then((accountAttributes) => {
        if (accountAttributes) {
          replace({
            ...accountAttributes,
            paid: accountAttributes?.paid || false,
          });
          sessionStorage.setItem('account', accountAttributes.account.toString());
          sessionStorage.setItem('loggedIn', accountAttributes.loggedIn.toString());

          if (getCookieValue('hasLoggedInBefore') !== 'true') {
            setCookie({
              cookieName: 'hasLoggedInBefore',
              data: accountAttributes.loggedIn.toString(),
              maxAge: ONE_YEAR,
            });
          }
        }
      })
      .finally(() => {
        setInitialized(true);
      });
  }, [location]);

  return { ...authenticationState, isInitialized };
};

const AuthenticationInitializer = () => {
  useInitializeLoggedInUser();
  return null;
};

export default AuthenticationInitializer; 
